import classNames from "classnames";
import { every, get, isEmpty, mapValues } from "lodash";
import { ActionIcon } from "PFComponents/action_icon";
import { Typography } from "PFComponents/typography";
import { ConfigTemplateInputItem } from "PFTypes/config_template";
import React from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { PropertyFieldProps } from "../property";
import { ArrayFieldEntry } from "./array_field_entry";
import css from "./properties_fields.module.scss";

const getArrayFieldItemDefaultValue = ({ type, properties }: ConfigTemplateInputItem) => {
  if (type === "string") {
    return "";
  }
  if (type === "object") {
    return mapValues(properties, () => "");
  }

  return "";
};

export const ArrayField = ({ fieldName, label, required, disabled, items }: PropertyFieldProps) => {
  const {
    control,
    formState: { errors },
    watch
  } = useFormContext();

  const { t } = useTranslation("saasAdmin");

  const { fields, append, remove } = useFieldArray({
    control,
    name: fieldName,
    rules: { required: required && t("filtersConfig.errors.required") }
  });

  const handleRemove = (index: number) => remove(index);

  const error = get(errors, fieldName)?.root?.message as string;

  const handleAddOption = (event) => {
    if (!items) {
      return;
    }
    event.preventDefault();
    append(getArrayFieldItemDefaultValue(items));
  };

  if (disabled) {
    return null;
  }

  const arrayValue = watch(fieldName);

  const isAddingAllowed =
    arrayValue === undefined ||
    arrayValue === null ||
    arrayValue.length === 0 ||
    every(arrayValue, (value) => !isEmpty(value));

  return (
    <div>
      <div
        className={classNames(css.arrayField, {
          [css.errorLabel]: error
        })}
      >
        <Typography variant="labelRegular">{label}</Typography>
        {fields.map((field, index) => (
          <ArrayFieldEntry
            key={field.id}
            entryFieldName={`${fieldName}.${index}`}
            onRemove={() => handleRemove(index)}
            index={index}
            {...(items || { type: "string" })}
          />
        ))}
        {isAddingAllowed && <ActionIcon name="add" onClick={handleAddOption} />}
      </div>
      {error && (
        <div className={css.error}>
          <Typography variant="labelRegular">{error}</Typography>
        </div>
      )}
    </div>
  );
};
